/**
 * The three notions of "current" — consequence first, cards second.
 *
 * Three changes that matter more than the cards themselves:
 *
 *   IT COLLAPSES WHEN THE ANSWERS AGREE. On a stable document all three point
 *   at the same revision, and that is most of the register. A header that
 *   shouts three cards at a non-problem gets ignored, and then it is ignored on
 *   the one document where it matters. So agreement collapses to a single
 *   sentence and the breakdown is one click away. The divergence badge means
 *   something because its absence means something.
 *
 *   IT LEADS WITH THE CONSEQUENCE. "Three different answers" is a data
 *   observation. "Site is building to Rev B, two revisions behind engineering;
 *   Rev 0 is four days from deemed receipt" is an action with an owner. The
 *   sentence is derived server-side so the reader never does arithmetic.
 *
 *   CURRENT FOR CONSTRUCTION COMES FIRST. Readers take the first card as the
 *   answer, and this is the one with a safety consequence.
 *
 * LABELS ARE PROVISIONAL. "Latest approved" is ambiguous in EPC usage —
 * approved by whom, for what — and an engineer reads it as approved for
 * construction, which is the exact conflation this model exists to prevent.
 * These wordings are a first pass to be tested on a document control desk.
 */
import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock, HardHat, Layers,
} from 'lucide-react';
import { day } from '@/components/nfa/nfaTheme';

const NOTIONS = [
  {
    key: 'currentForConstruction', id: 'cfc', icon: HardHat, lead: true,
    title: 'Released to site',
    formal: 'current for construction',
    question: 'What is site entitled to build from?',
    audience: 'Construction',
    detail: 'The highest revision that permits construction AND has reached the '
      + 'constructing party. Issued is not received.',
  },
  {
    key: 'latestApprovedRevision', id: 'approved', icon: CheckCircle2,
    title: 'Latest accepted by review',
    formal: 'latest approved revision',
    question: 'What has the reviewing party accepted?',
    audience: 'Cost, schedule, procurement',
    detail: 'The highest revision whose status code counts as approved. Acceptance by '
      + 'review is not a licence to build.',
  },
  {
    key: 'latestRevision', id: 'latest', icon: Layers,
    title: 'Latest revision',
    formal: 'latest revision',
    question: 'What is the newest issue of this document?',
    audience: 'Document control',
    detail: 'The highest revision in sequence, whatever its status.',
  },
];

const Countdown = ({ t, awaiting }) => {
  if (!awaiting) return null;
  const d = awaiting.daysRemaining;
  return (
    <div className="mt-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px]"
      data-testid="cfc-countdown"
      style={{ background: t.tint(t.amber, '16'), color: t.amber,
        border: `1px solid ${t.tint(t.amber, '33')}` }}>
      <Clock size={12} className="shrink-0" />
      <span>
        <b>Transmittal outstanding</b> — Rev {awaiting.revision_code}
        {awaiting.transmitted
          ? (d ? `, ${d} day${d === 1 ? '' : 's'} to deemed receipt (${awaiting.deemedOn})`
            : ', awaiting acknowledgement')
          : ' permits construction but has not been transmitted'}
        {awaiting.to_org ? ` · ${awaiting.to_org}` : ''}
      </span>
    </div>
  );
};

const Card = ({ t, spec, value, awaiting }) => {
  const Icon = spec.icon;
  const tone = value ? (spec.lead ? t.green : t.primary) : t.muted;
  return (
    <div
      data-testid={`notion-card-${spec.id}`}
      className="relative flex flex-col rounded-xl p-4 transition-colors duration-200"
      style={{
        background: value ? `linear-gradient(160deg, ${t.tint(tone, '1F')}, ${t.surface} 65%)`
          : t.surfaceAlt,
        border: `1px solid ${value ? t.tint(tone, '55') : t.border}`,
        boxShadow: spec.lead && value ? `0 0 0 1px ${t.tint(tone, '33')}` : 'none',
      }}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: tone }} />
        <span className="font-mono text-[10px] uppercase tracking-wider"
          style={{ color: t.muted }}>{spec.audience}</span>
      </div>
      <div className="mt-1 text-sm font-semibold" style={{ color: t.ink }}>
        {spec.title}
      </div>
      <div className="font-mono text-[10px]" style={{ color: t.muted }}>
        {spec.formal}
      </div>
      <div className="mt-1 text-xs" style={{ color: t.body }}>{spec.question}</div>

      <div className="mt-3 flex items-baseline gap-2">
        {value ? (
          <>
            <span data-testid={`notion-value-${spec.id}`}
              className="font-mono text-3xl font-bold tracking-tight" style={{ color: tone }}>
              {value.revision_code}
            </span>
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded"
              style={{ background: t.tint(tone, '22'), color: tone }}>
              {value.status_code ? `${value.status_code} · ${value.statusLabel || ''}`
                : 'no status yet'}
            </span>
          </>
        ) : (
          <span data-testid={`notion-value-${spec.id}`} className="text-sm font-medium"
            style={{ color: t.muted }}>None</span>
        )}
      </div>

      {value && (
        <div className="mt-1.5 font-mono text-[10px]" style={{ color: t.muted }}>
          {value.issue_date ? `issued ${day(value.issue_date)}` : 'no issue date'}
          {value.closed_out_by_name && ` · stamped by ${value.closed_out_by_name}`}
        </div>
      )}

      {spec.lead && (
        <>
          {value && (
            <div className="mt-2 rounded-md px-2 py-1.5 text-[11px] leading-snug"
              data-testid="notion-cfc-basis"
              style={{ background: t.tint(tone, '14'), color: t.body }}>
              {value.acknowledgementStatement}
              {value.acknowledgementBasis === 'deemed' && (
                <span className="ml-1 font-mono uppercase tracking-wider"
                  style={{ color: t.amber }}>deemed — weaker evidence</span>
              )}
              {value.acknowledgementBasis === 'explicit' && (
                <span className="ml-1 font-mono uppercase tracking-wider"
                  style={{ color: t.green }}>explicit</span>
              )}
            </div>
          )}
          <Countdown t={t} awaiting={awaiting} />
        </>
      )}

      <div className="mt-auto pt-2 text-[10px] leading-snug" style={{ color: t.muted }}>
        {spec.detail}
      </div>
    </div>
  );
};

export const EdmCurrentNotions = ({ t, data }) => {
  const distinct = data?.distinctNotions ?? 0;
  const disagree = distinct > 1;
  const [open, setOpen] = useState(false);
  const show = disagree || open;
  const c = data?.consequence || {};
  const sev = { gap: t.red, watch: t.amber, ok: t.green, none: t.muted }[c.severity] || t.muted;
  const SevIcon = c.severity === 'ok' ? CheckCircle2 : AlertTriangle;

  return (
    <section data-testid="edm-current-notions">
      {/* the consequence, first */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <SevIcon size={18} className="mt-0.5 shrink-0" style={{ color: sev }} />
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-semibold leading-snug"
              style={{ color: t.ink }} data-testid="consequence-headline">
              {c.headline || 'Which revision is current?'}
            </h2>
            {c.awaiting?.daysRemaining != null && c.awaiting.transmitted && (
              <div className="mt-1 font-mono text-[11px]" style={{ color: t.amber }}
                data-testid="consequence-countdown">
                {c.awaiting.daysRemaining} day
                {c.awaiting.daysRemaining === 1 ? '' : 's'} remaining · deemed received
                {' '}{c.awaiting.deemedOn} unless acknowledged first
              </div>
            )}
          </div>
        </div>
        <span
          data-testid="notions-agreement-badge"
          className="shrink-0 font-mono text-[10px] uppercase tracking-wider px-2 py-1
                     rounded-full"
          style={{
            background: t.tint(disagree ? t.amber : t.green, '1F'),
            color: disagree ? t.amber : t.green,
            border: `1px solid ${t.tint(disagree ? t.amber : t.green, '44')}`,
          }}>
          {disagree ? `${distinct} different answers` : 'all three agree'}
        </span>
      </div>

      {/* the collapsed statement, when the three answers are one */}
      {!disagree && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3"
          data-testid="notions-collapsed"
          style={{ background: t.surface, border: `1px solid ${t.border}` }}>
          <span className="font-mono text-2xl font-bold tracking-tight"
            style={{ color: t.green }} data-testid="notions-collapsed-revision">
            {data?.latestRevision?.revision_code || '—'}
          </span>
          <span className="text-sm" style={{ color: t.body }}>
            latest revision, latest accepted by review, and released to site
            {data?.latestRevision?.status_code
              && ` · ${data.latestRevision.status_code} ${data.latestRevision.statusLabel || ''}`}
          </span>
          <button
            type="button"
            data-testid="notions-expand-toggle"
            onClick={() => setOpen((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1
                       font-mono text-[10px] uppercase tracking-wider transition-colors"
            style={{ color: t.primary, border: `1px solid ${t.tint(t.primary, '44')}` }}>
            {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {open ? 'hide the three questions' : 'show the three questions'}
          </button>
        </div>
      )}

      {show && (
        <>
          {disagree && (
            <p className="mt-2 max-w-3xl text-xs md:text-sm" style={{ color: t.body }}>
              These are three different questions and on this document they have
              {' '}{distinct} different answers. Nothing is stored as current — all three
              are worked out each time this page loads.
            </p>
          )}
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {NOTIONS.map((spec) => (
              <Card key={spec.id} t={t} spec={spec} value={data?.[spec.key]}
                awaiting={spec.lead ? c.awaiting : null} />
            ))}
          </div>
          <p className="mt-2 font-mono text-[10px]" style={{ color: t.muted }}
            data-testid="notions-labels-provisional">
            Labels provisional — &ldquo;released to site&rdquo; and &ldquo;accepted by
            review&rdquo; still to be read by a document controller before they are fixed.
          </p>
        </>
      )}
    </section>
  );
};

export default EdmCurrentNotions;
